import { Capacitor } from "@capacitor/core";
import { useId, useMemo, useState, useSyncExternalStore } from "react";
import { useAuth } from "../lib/auth/auth-context";
import { formatTitleDate, formatTitleTime } from "../lib/captures/labels";
import { createPendingRecordingStore } from "../lib/captures/pending-recording";
import { capacitorPreferencesAdapter } from "../lib/captures/preferences-adapter";
import {
  finishRecording,
  type RecordingActionDeps,
  type RecordingToFinish,
  recordingsToFinishSnapshot,
  subscribeRecordingsToFinish,
  trashRecording,
} from "../lib/captures/recordings-to-finish";
import { TAKE_COPY } from "../lib/captures/take-copy";
import { requestTakesRun, sharedTakeOverrides } from "../lib/captures/take-runtime";
import { createTrashMetaStore, formatTrashDuration } from "../lib/captures/trash";
import { createTakeRecorder } from "../lib/native/take-recorder";
import { useCurrentProject } from "../lib/projects/project-context";
import type { Project } from "../lib/projects/schema";
import { TakeProjectPicker } from "./TakeProjectPicker";
import { Button } from "./ui/Button";

// Accueil, « Enregistrement retrouvé » (anti-perte phase 1, A9, spec §4 et
// §5.2.4) : audio resté sur le téléphone sans capture complète — orphelins des
// anciennes versions, audio finalisé jamais rattaché (2026-08-17), capture
// tronquée dont les segments sont restés à côté (24-09 : 30 s en base, 89 min
// sur le téléphone). Une carte par enregistrement repéré par le filet global.
//
// Même motif que les notices d'accueil (liseré + `role="status"`, DS §6.10),
// montée sous les boutons d'enregistrement (DS §6.11). « Sauvegarder
// l'enregistrement » en primaire ; « Mettre à la corbeille » en ghost (7 jours,
// rien détruit, DS §6.15). Projet inconnu : le sélecteur, dans la carte.

function productionDeps(userId: string): RecordingActionDeps {
  return {
    recorder: createTakeRecorder(),
    overrides: sharedTakeOverrides(),
    pendingStore: createPendingRecordingStore(capacitorPreferencesAdapter),
    trashMeta: createTrashMetaStore(capacitorPreferencesAdapter),
    userId,
    now: () => new Date(),
    requestRun: requestTakesRun,
  };
}

// Projet déjà connu : celui de la capture à compléter, ou celui inscrit au
// départ dans la trace de l'audio finalisé.
function knownProjectId(r: RecordingToFinish): string | null {
  return r.capture?.projectId ?? r.pending?.projectId ?? null;
}

interface FinishRecordingCardProps {
  recording: RecordingToFinish;
  projects: Project[];
  loading: boolean;
  erreur: string | null;
  onFinish: (projectId: string | null) => Promise<boolean>;
  onTrash: () => Promise<boolean>;
}

export function FinishRecordingCard({
  recording,
  projects,
  loading,
  erreur,
  onFinish,
  onTrash,
}: FinishRecordingCardProps) {
  const titleId = useId();
  const [projectId, setProjectId] = useState("");
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const needsPicker = knownProjectId(recording) === null;
  // Le sélecteur n'a de projet à proposer que si le chargement est fini sans
  // échec (17 août : « 200 [] » pendant le boot ≠ « aucun projet »).
  const canSave = !needsPicker || (!loading && erreur === null && projects.length > 0);
  // Date en mois ENTIER et heure au format du titre par défaut des captures.
  const startedAt = recording.pending?.startedAt ?? recording.item.startedAt;
  const title = TAKE_COPY.toFinishTitle(
    formatTitleDate(startedAt),
    formatTitleTime(startedAt),
    formatTrashDuration(recording.item.totalMs),
  );

  async function run(action: () => Promise<boolean>) {
    setBusy(true);
    setFailed(false);
    const ok = await action();
    // Réussi : la carte quitte la liste. Refusé : rien n'a bougé, on le dit.
    if (!ok) {
      setFailed(true);
      setBusy(false);
    }
  }

  return (
    <div
      role="status"
      className="flex flex-col gap-2 rounded-r-md border-l-2 border-flambeau-400 bg-flambeau-50 px-3 py-3 text-small text-ink"
    >
      <p className="m-0 flex items-center gap-2 text-label font-semibold uppercase tracking-wide text-flambeau-700">
        <span aria-hidden className="inline-block size-2.5 rounded-full bg-flambeau-500" />
        {TAKE_COPY.foundHeader}
      </p>
      <p id={titleId} className="m-0 font-medium">
        {title}
      </p>
      {needsPicker && (
        <TakeProjectPicker
          projects={projects}
          loading={loading}
          erreur={erreur}
          value={projectId}
          onChange={setProjectId}
          disabled={busy}
        />
      )}
      <div className="flex flex-wrap justify-end gap-2">
        {canSave && (
          <Button
            variant="primary"
            size="sm"
            aria-describedby={titleId}
            disabled={busy || (needsPicker && !projectId)}
            onClick={() => void run(() => onFinish(needsPicker ? projectId : null))}
          >
            {TAKE_COPY.finish}
          </Button>
        )}
        {/* Un ghost n'est jamais la seule affordance d'une rangée (DS §6.2). */}
        <Button
          variant={canSave ? "ghost" : "secondary"}
          size="sm"
          aria-describedby={titleId}
          disabled={busy}
          onClick={() => void run(onTrash)}
        >
          {TAKE_COPY.ignore}
        </Button>
      </div>
      {failed && (
        <p role="alert" className="m-0 text-red-deep">
          {TAKE_COPY.actionFailed}
        </p>
      )}
    </div>
  );
}

interface FinishRecordingCardsProps {
  /** Dépendances injectables (tests) ; défaut = natif et magasins de l'app. */
  deps?: RecordingActionDeps;
}

/** Les cartes « Enregistrement retrouvé » publiées par le filet global. */
export function FinishRecordingCards({ deps }: FinishRecordingCardsProps) {
  const { user } = useAuth();
  const { projects, loading, erreur } = useCurrentProject();
  const recordings = useSyncExternalStore(subscribeRecordingsToFinish, recordingsToFinishSnapshot);
  const userId = user?.id ?? null;
  const resolved = useMemo(() => deps ?? (userId ? productionDeps(userId) : null), [deps, userId]);
  if (!Capacitor.isNativePlatform() || !resolved || recordings.length === 0) return null;
  return (
    <div className="flex flex-col gap-3">
      {recordings.map((r) => (
        <FinishRecordingCard
          key={r.item.groupId}
          recording={r}
          projects={projects}
          loading={loading}
          erreur={erreur}
          onFinish={(projectId) => finishRecording(resolved, r, projectId)}
          onTrash={() => trashRecording(resolved, r)}
        />
      ))}
    </div>
  );
}
